import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { appRoutes } from '@/routes';
import moment from 'moment-timezone';
import { useFormChanges } from '../../hooks/useFormChanges';
import { ROLE_OPTIONS } from '@/constants/roles';
import { useEmployeePageCompanyTeamUrlSync } from '@/hooks/useEmployeePageUrlSync';
import { useUrlParamSync } from '@/hooks/useUrlParamSync';
import { mergeEmployeePageFiltersIntoSearch } from '@/utils/employeePageUrlFilters';
import { EMPLOYEE_PAGE_QUERY } from '@/constants/urlQuery';
import { IntegrationImportTabDemoComponent } from './components/IntegrationImportTab.demo.component';
import {
  addNotification,
  notificationStates,
} from '../../components/Notification';
import {
  FiMail,
  FiCreditCard,
  FiGlobe,
  FiMonitor,
  FiHash,
  FiCalendar,
  FiShield,
  FiEye,
  FiEdit2,
  FiUserPlus,
  FiRefreshCw,
  FiTrash2,
} from 'react-icons/fi';
import { BsBuildings } from 'react-icons/bs';
import { escapeCSVField, downloadCSV } from '@/utils/csvExport';
import { EmployeeListView } from './EmployeeList.view';
import {
  DEMO_COMPANY_OPTIONS,
  DEMO_SELECTED_COMPANY,
  DEMO_TEAM_OPTIONS,
  DEMO_SELECTED_TEAM,
  DEMO_EMPLOYEES,
} from './EmployeeList.demoData';

// The Employee List page in demo mode never talks to Firestore/Redux/
// services for its roster or company/department data — every dialog below
// mutates the local `employeesState` array instead, seeded from the static
// DEMO_EMPLOYEES fixture. This mirrors the local-mutation approach the old
// demo-v2 rewrite used (shared `updateEmployee` / `removeEmployeeFromState`
// callbacks reused by every dialog), reshaped to match the prop contract of
// the current (real) EmployeeListView.

const ROLE_FILTER_OPTIONS = [
  { label: 'All Roles', value: 'All' },
  { label: 'Admin', value: 'Admin' },
  { label: 'Manager', value: 'Manager' },
  { label: 'Member', value: 'Member' },
];
const ROLE_FILTER_VALUES = ROLE_FILTER_OPTIONS.map((o) => o.value);
const normalizeRoleFilter = (value) =>
  ROLE_FILTER_VALUES.includes(value) ? value : 'All';

const PAGE_SIZE = 10;

/** Look up a department's label from its id. Falls back to the id itself. */
const teamLabelFor = (teamId) =>
  DEMO_TEAM_OPTIONS.find((t) => t.value === teamId)?.label || teamId;

/** Build { label, value } dropdown options from an array of team ids. */
const buildTeamOptionsFromIds = (teamIds) =>
  (teamIds || []).map((id) => ({ label: teamLabelFor(id), value: id }));

export const EmployeeListDemoComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const authState = useSelector((state) => ({ ...state.auth }));
  const userProfile = authState.profile;
  const activeCompanyId = userProfile?.activeCompany?.companyId;
  const activeTeamId = useSelector(
    (s) => s.team?.active?.activeTeamid ?? s.team?.active?.activeTeamId
  );
  const userRole = userProfile?.role || '';
  const showAddTeamButton = userRole === 'Admin';
  const companyList = userProfile?.companyList || [];
  const userUID = userProfile?.id;
  const toast = useRef(null);
  const menuRef = useRef(null);
  const editFormHydratedRef = useRef(null);

  // The full roster lives here — every dialog below mutates this array
  // directly instead of dispatching a Redux action / calling a service.
  const [employeesState, setEmployeesState] = useState(() =>
    DEMO_EMPLOYEES.map((emp) => ({ ...emp, teamIds: [...emp.teamIds] }))
  );

  const updateEmployee = useCallback((userId, patch) => {
    setEmployeesState((prev) =>
      prev.map((emp) =>
        emp.userId === userId
          ? { ...emp, ...(typeof patch === 'function' ? patch(emp) : patch) }
          : emp
      )
    );
  }, []);

  const removeEmployeeFromState = useCallback((userId) => {
    setEmployeesState((prev) => prev.filter((emp) => emp.userId !== userId));
  }, []);

  // State
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [error, setError] = useState(null);
  const [moreDetailsUserId, setMoreDetailsUserId] = useState(null);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDeskId, setEditDeskId] = useState('');
  const [editTimeZone, setEditTimeZone] = useState('');
  const [editRole, setEditRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [activeTab, setActiveTab] = useState('employees');
  const [pickActiveTeamDialogVisible, setPickActiveTeamDialogVisible] =
    useState(false);
  const [replacementTeamOptions, setReplacementTeamOptions] = useState([]);
  const [selectedReplacementTeam, setSelectedReplacementTeam] = useState(null);
  const [pendingDeleteContext, setPendingDeleteContext] = useState(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');
  const [deleteConfirmPayload, setDeleteConfirmPayload] = useState(null);
  const [removeFromCompanyConfirmVisible, setRemoveFromCompanyConfirmVisible] =
    useState(false);
  const [removeFromCompanyMessage, setRemoveFromCompanyMessage] = useState('');
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [addToAnotherTeamDialogVisible, setAddToAnotherTeamDialogVisible] =
    useState(false);
  const [addToAnotherTeamUser, setAddToAnotherTeamUser] = useState(null);
  // Existing company-wide admins already have access to every team, so the
  // "add to another company & department" membership step doesn't apply to
  // them — the dialog collapses to just the active tracking section.
  const isExistingTeamAdmin = addToAnotherTeamUser?.role === 'Admin';
  const [addToAnotherTeamCompany, setAddToAnotherTeamCompany] = useState(null);
  const [addToAnotherTeamRole, setAddToAnotherTeamRole] = useState(null);
  const [addToAnotherTeamTeams, setAddToAnotherTeamTeams] = useState([]);
  const [addToAnotherTeamCandidateTeams, setAddToAnotherTeamCandidateTeams] =
    useState([]);
  const [
    addToAnotherTeamCompanyTeamCount,
    setAddToAnotherTeamCompanyTeamCount,
  ] = useState(0);
  const [
    addToAnotherTeamActiveCompanyOptions,
    setAddToAnotherTeamActiveCompanyOptions,
  ] = useState([]);
  const [addToAnotherTeamActiveCompany, setAddToAnotherTeamActiveCompany] =
    useState(null);
  const [
    addToAnotherTeamActiveTeamOptions,
    setAddToAnotherTeamActiveTeamOptions,
  ] = useState([]);
  const [addToAnotherTeamActiveTeam, setAddToAnotherTeamActiveTeam] =
    useState(null);
  const [addToAnotherTeamActiveBaseline, setAddToAnotherTeamActiveBaseline] =
    useState(null);

  const [changeActiveTeamDialogVisible, setChangeActiveTeamDialogVisible] =
    useState(false);
  const [changeActiveTeamUser, setChangeActiveTeamUser] = useState(null);
  const [changeActiveTeamCompanyOptions, setChangeActiveTeamCompanyOptions] =
    useState([]);
  const [changeActiveTeamCompany, setChangeActiveTeamCompany] = useState(null);
  const [changeActiveTeamTeamOptions, setChangeActiveTeamTeamOptions] =
    useState([]);
  const [changeActiveTeamTeam, setChangeActiveTeamTeam] = useState(null);
  const [changeActiveTeamBaseline, setChangeActiveTeamBaseline] =
    useState(null);

  const addTeamDefaultCompanyId = DEMO_SELECTED_COMPANY.value;

  const timezoneOptions = moment.tz
    .names()
    .map((tz) => ({ label: tz, value: tz }));

  // Company/department dropdown data is a fixed, in-memory fixture — no
  // loading state, no Firestore roster fetch.
  const companyOptions = DEMO_COMPANY_OPTIONS;
  const teamOptions = DEMO_TEAM_OPTIONS;

  useEmployeePageCompanyTeamUrlSync({
    companyOptions,
    teamOptions,
    selectedCompany,
    selectedTeam,
    setSelectedCompany,
    setSelectedTeam,
    history,
    location,
    defaultCompanyId: activeCompanyId,
    defaultTeamId: activeTeamId,
    teamDefaultOnlyWhenCompanyId: activeCompanyId,
    searchTerm,
    roleFilter,
  });
  useUrlParamSync({
    location,
    paramKey: EMPLOYEE_PAGE_QUERY.search,
    setValue: setSearchTerm,
  });
  useUrlParamSync({
    location,
    paramKey: EMPLOYEE_PAGE_QUERY.role,
    setValue: setRoleFilter,
    normalize: normalizeRoleFilter,
  });

  // Real company/team membership never matches the demo profile (no
  // Firestore record backs it), so fall back to the single demo company and
  // its first department until the user actually picks one from the pills.
  const displaySelectedCompany = selectedCompany || DEMO_SELECTED_COMPANY;
  const displaySelectedTeam = selectedTeam || DEMO_SELECTED_TEAM;

  // Roster for the currently selected department.
  const employees = useMemo(
    () =>
      employeesState.filter((emp) =>
        emp.teamIds.includes(displaySelectedTeam?.value)
      ),
    [employeesState, displaySelectedTeam?.value]
  );

  const roleCounts = useMemo(
    () =>
      (employees || []).reduce(
        (acc, emp) => {
          acc.total += 1;
          if (emp.role === 'Admin') acc.admins += 1;
          if (emp.role === 'Manager') acc.managers += 1;
          return acc;
        },
        { total: 0, admins: 0, managers: 0 }
      ),
    [employees]
  );

  const onTeamChange = useCallback(
    (val) => {
      setSelectedTeam(teamOptions.find((o) => o.value === val) || null);
      setError(null);
    },
    [teamOptions]
  );

  const showDeleteConfirm = useCallback(({ message, payload }) => {
    setDeleteConfirmMessage(message);
    setDeleteConfirmPayload(payload);
    setDeleteConfirmVisible(true);
  }, []);

  const resetDeleteConfirm = useCallback(() => {
    setDeleteConfirmVisible(false);
    setDeleteConfirmMessage('');
    setDeleteConfirmPayload(null);
  }, []);

  const confirmDeleteUserFromTeam = useCallback(() => {
    if (deleteConfirmPayload) {
      const { userId, teamId, newActiveTeamId } = deleteConfirmPayload;
      updateEmployee(userId, (emp) => ({
        teamIds: emp.teamIds.filter((t) => t !== teamId),
        activeTeamId: newActiveTeamId || emp.activeTeamId,
      }));
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'User removed from department',
        life: 3000,
      });
    }
    resetDeleteConfirm();
  }, [deleteConfirmPayload, updateEmployee, resetDeleteConfirm]);

  const resetPickActiveTeamDialog = useCallback(() => {
    setPickActiveTeamDialogVisible(false);
    setReplacementTeamOptions([]);
    setSelectedReplacementTeam(null);
    setPendingDeleteContext(null);
  }, []);

  const resetAddToAnotherTeamDialog = useCallback(() => {
    setAddToAnotherTeamDialogVisible(false);
    setAddToAnotherTeamUser(null);
    setAddToAnotherTeamRole(null);
    setAddToAnotherTeamTeams([]);
    setAddToAnotherTeamCandidateTeams([]);
    setAddToAnotherTeamCompanyTeamCount(0);
    setAddToAnotherTeamActiveCompanyOptions([]);
    setAddToAnotherTeamActiveCompany(null);
    setAddToAnotherTeamActiveTeamOptions([]);
    setAddToAnotherTeamActiveTeam(null);
    setAddToAnotherTeamActiveBaseline(null);
  }, []);

  const resetChangeActiveTeamDialog = useCallback(() => {
    setChangeActiveTeamBaseline(null);
    setChangeActiveTeamDialogVisible(false);
    setChangeActiveTeamUser(null);
    setChangeActiveTeamCompanyOptions([]);
    setChangeActiveTeamCompany(null);
    setChangeActiveTeamTeamOptions([]);
    setChangeActiveTeamTeam(null);
  }, []);

  const openChangeActiveTeamDialog = useCallback(() => {
    if (!editRow?.userId) {
      addNotification({
        message: 'Missing user context',
        level: notificationStates.warning,
      });
      return;
    }
    menuRef.current?.hide();
    const employee = employeesState.find((e) => e.userId === editRow.userId);
    if (!employee) return;

    setChangeActiveTeamUser(employee);
    setChangeActiveTeamCompanyOptions(DEMO_COMPANY_OPTIONS);
    setChangeActiveTeamCompany(DEMO_SELECTED_COMPANY);
    const teamOpts = buildTeamOptionsFromIds(employee.teamIds);
    setChangeActiveTeamTeamOptions(teamOpts);
    const selected =
      teamOpts.find((t) => t.value === employee.activeTeamId) ||
      teamOpts[0] ||
      null;
    setChangeActiveTeamTeam(selected);
    setChangeActiveTeamBaseline({
      companyId: DEMO_SELECTED_COMPANY.value,
      teamId: selected?.value || null,
    });
    setChangeActiveTeamDialogVisible(true);
  }, [editRow, employeesState]);

  const onChangeActiveTeamCompanySelect = useCallback(
    (option) => {
      if (!changeActiveTeamUser?.userId || !option?.value) return;
      setChangeActiveTeamCompany(option);
      const teamOpts = buildTeamOptionsFromIds(changeActiveTeamUser.teamIds);
      setChangeActiveTeamTeamOptions(teamOpts);
      setChangeActiveTeamTeam(teamOpts[0] || null);
    },
    [changeActiveTeamUser]
  );

  const submitChangeActiveTeam = useCallback(() => {
    if (
      !changeActiveTeamUser?.userId ||
      !changeActiveTeamCompany?.value ||
      !changeActiveTeamTeam?.value
    ) {
      addNotification({
        message: 'Select company and active department',
        level: notificationStates.warning,
      });
      return;
    }
    updateEmployee(changeActiveTeamUser.userId, {
      activeTeamId: changeActiveTeamTeam.value,
    });
    addNotification({
      message: `Active department saved: ${changeActiveTeamTeam.label}`,
      level: notificationStates.success,
    });
    resetChangeActiveTeamDialog();
  }, [
    changeActiveTeamUser,
    changeActiveTeamCompany,
    changeActiveTeamTeam,
    updateEmployee,
    resetChangeActiveTeamDialog,
  ]);

  const hasChangeActiveTeamPendingSave = useMemo(() => {
    if (
      !changeActiveTeamBaseline?.companyId ||
      !changeActiveTeamCompany?.value ||
      !changeActiveTeamTeam?.value
    ) {
      return false;
    }
    const companyChanged =
      changeActiveTeamCompany.value !== changeActiveTeamBaseline.companyId;
    const baselineTeam = changeActiveTeamBaseline.teamId ?? '';
    const currentTeam = changeActiveTeamTeam.value ?? '';
    const teamChanged = String(currentTeam) !== String(baselineTeam);
    return companyChanged || teamChanged;
  }, [changeActiveTeamBaseline, changeActiveTeamCompany, changeActiveTeamTeam]);

  // Filter roster (name, email, role)
  const filteredEmployees = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    return (employees || []).filter((emp) => {
      if (roleFilter !== 'All' && emp.role !== roleFilter) return false;
      if (!term) return true;
      const name = (emp.name || '').toLowerCase();
      const email = (emp.email || '').toLowerCase();
      const role = (emp.role || '').toLowerCase();
      return name.includes(term) || email.includes(term) || role.includes(term);
    });
  }, [employees, searchTerm, roleFilter]);

  const [sortedBy, setSortedBy] = useState(null);
  const [reverseSort, setReverseSort] = useState(false);
  const handleSortClick = (id, reverse) => {
    setSortedBy(id);
    setReverseSort(reverse);
  };

  const sortedEmployees = useMemo(() => {
    if (!sortedBy) return filteredEmployees;
    const sorted = [...filteredEmployees].sort((a, b) =>
      (a[sortedBy] || '').localeCompare(b[sortedBy] || '')
    );
    return reverseSort ? sorted.reverse() : sorted;
  }, [filteredEmployees, sortedBy, reverseSort]);

  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    roleFilter,
    sortedBy,
    reverseSort,
    displaySelectedTeam?.value,
  ]);

  const totalPages = Math.max(1, Math.ceil(sortedEmployees.length / PAGE_SIZE));
  const pagedEmployees = sortedEmployees.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const moreDetailsRosterRow = useMemo(() => {
    if (!moreDetailsUserId) return null;
    return employeesState.find((e) => e.userId === moreDetailsUserId) || null;
  }, [moreDetailsUserId, employeesState]);

  // Mirrors the real container's `userIdForExtendedFetch` + react-query
  // "extended details" concept, but resolved synchronously off the local
  // roster — every demo employee already carries employeeId/timeZone/deskId/
  // guid/joinedOn directly (see EmployeeList.demoData.js), so there is
  // nothing async to fetch, load, or fail.
  const userIdForExtendedFetch = moreDetailsUserId
    ? moreDetailsUserId
    : editDialogVisible && editRow?.userId
      ? editRow.userId
      : null;

  const extendedDetails = useMemo(() => {
    if (!userIdForExtendedFetch) return null;
    const emp = employeesState.find((e) => e.userId === userIdForExtendedFetch);
    if (!emp) return null;
    return {
      employeeId: emp.employeeId,
      timeZone: emp.timeZone,
      deskId: emp.deskId,
      guid: emp.guid,
      createdAt: emp.joinedOn,
    };
  }, [userIdForExtendedFetch, employeesState]);
  const loadingExtendedDetails = false;
  const extendedDetailsError = false;
  const extendedDetailsQueryError = null;

  // Check if user is Admin
  useEffect(() => {
    const isAdmin =
      userProfile?.activeCompany?.role === 'Admin' ||
      companyList.some((company) => company.role === 'Admin');

    if (!isAdmin && !authState.loading) {
      setError('Access denied. Only Admin users can view employee list.');
    }
  }, [userProfile, companyList, authState.loading]);

  // When company is cleared, reset team
  useEffect(() => {
    if (!selectedCompany?.value) {
      setSelectedTeam(null);
    }
  }, [selectedCompany?.value]);

  // CopyIconButton callbacks — shared by every copyable field in the "Employee details" popup
  const handleFieldCopied = (label) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Copied',
      detail: `${label} copied to clipboard`,
      life: 2000,
    });
  };

  const handleFieldCopyError = (err, label) => {
    console.error(`Failed to copy ${label}:`, err);
    addNotification({
      message: `Failed to copy ${label}`,
      level: notificationStates.error,
    });
  };

  const formatJoinedOn = (createdAt) => {
    if (!createdAt) return 'N/A';
    const date =
      typeof createdAt?.toDate === 'function'
        ? createdAt.toDate()
        : new Date(createdAt);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return moment(date).format('D MMM, YYYY');
  };

  // Export to CSV (exports current view: filtered list when search is active)
  const exportToCSV = () => {
    if (filteredEmployees.length === 0) {
      addNotification({
        message: searchTerm.trim()
          ? 'No data to export (no employees match your search).'
          : 'No data to export',
        level: notificationStates.warning,
      });
      return;
    }

    const companyName = displaySelectedCompany?.label || 'company';
    const teamName = displaySelectedTeam?.label || 'department';
    const date = new Date().toISOString().split('T')[0];
    const filename =
      `employee-list-${companyName}-${teamName}-${date}.csv`.replace(
        /[^a-z0-9-]/gi,
        '-'
      );

    const headers = ['Name', 'Email', 'Role'];
    const rows = filteredEmployees.map((emp) =>
      [
        escapeCSVField(emp.name),
        escapeCSVField(emp.email),
        escapeCSVField(emp.role),
      ].join(',')
    );
    downloadCSV({ headers, rows, filename });

    addNotification({
      message: 'CSV exported successfully',
      level: notificationStates.success,
    });
  };

  const onActionsButtonClick = (e, rowData) => {
    setEditRow(rowData);
    menuRef.current.toggle(e);
  };

  const openMoreDetailsDialog = useCallback(() => {
    if (editRow?.userId) setMoreDetailsUserId(editRow.userId);
    menuRef.current?.hide();
  }, [editRow?.userId]);

  const openEditDialog = useCallback(() => {
    editFormHydratedRef.current = null;
    setEditDialogVisible(true);
    menuRef.current?.hide();
  }, []);

  const openDeleteConfirm = () => {
    if (!editRow?.userId || !displaySelectedTeam?.value) return;
    menuRef.current.hide();

    const userId = editRow.userId;
    const teamId = displaySelectedTeam.value;
    const employee = employeesState.find((e) => e.userId === userId);
    if (!employee) return;

    // Rule (1): cannot remove if user is only in one department
    if (!employee.teamIds || employee.teamIds.length <= 1) {
      addNotification({
        message:
          'This user is only in one department. Add them to another department first, then delete.',
        level: notificationStates.warning,
      });
      return;
    }

    const isDeletingFromActiveTeam = employee.activeTeamId === teamId;

    // Rules (3) and (4): deleting from a non-active department keeps active department unchanged
    if (!isDeletingFromActiveTeam) {
      showDeleteConfirm({
        message:
          'Are you sure you want to delete this user from the department?',
        payload: { userId, teamId },
      });
      return;
    }

    const remainingTeamIds = employee.teamIds.filter((t) => t !== teamId);

    if (remainingTeamIds.length === 0) {
      addNotification({
        message:
          'Cannot delete user from their only department. Add them to another department first.',
        level: notificationStates.warning,
      });
      return;
    }

    // Rule (2): exactly two departments -> auto-switch active to the other one
    if (remainingTeamIds.length === 1) {
      showDeleteConfirm({
        message:
          'This user is being removed from their active department. The other department will be set as active. Continue?',
        payload: { userId, teamId, newActiveTeamId: remainingTeamIds[0] },
      });
      return;
    }

    // Rule (5): multiple remaining departments -> ask which one should become active
    const options = buildTeamOptionsFromIds(remainingTeamIds);
    setReplacementTeamOptions(options);
    setSelectedReplacementTeam(options[0] || null);
    setPendingDeleteContext({ userId, teamId });
    setPickActiveTeamDialogVisible(true);
  };

  const confirmDeleteAfterPickingActiveTeam = useCallback(() => {
    if (!pendingDeleteContext?.userId || !pendingDeleteContext?.teamId) {
      resetPickActiveTeamDialog();
      return;
    }
    if (!selectedReplacementTeam?.value) {
      addNotification({
        message: 'Please select which department should be made active.',
        level: notificationStates.warning,
      });
      return;
    }

    const payload = {
      ...pendingDeleteContext,
      newActiveTeamId: selectedReplacementTeam.value,
    };

    resetPickActiveTeamDialog();
    showDeleteConfirm({
      message:
        'This user is being removed from their active department. The selected department will be set as active. Continue?',
      payload,
    });
  }, [
    pendingDeleteContext,
    selectedReplacementTeam,
    resetPickActiveTeamDialog,
    showDeleteConfirm,
  ]);

  const openRemoveFromCompanyConfirm = () => {
    if (!editRow?.userId) return;
    menuRef.current?.hide();
    const companyName = displaySelectedCompany?.label || 'this company';
    const userName = editRow.name || editRow.email;
    setRemoveFromCompanyMessage(
      `Remove ${userName} from ${companyName}? They will be removed from all departments in this company and their license will be revoked.`
    );
    setRemoveFromCompanyConfirmVisible(true);
  };

  const resetRemoveFromCompanyConfirm = () => {
    setRemoveFromCompanyConfirmVisible(false);
    setRemoveFromCompanyMessage('');
  };

  const confirmRemoveFromCompany = () => {
    if (!editRow?.userId) return;
    removeEmployeeFromState(editRow.userId);
    resetRemoveFromCompanyConfirm();
    toast.current?.show({
      severity: 'success',
      summary: 'Success',
      detail: 'User removed from company and all its departments',
      life: 3000,
    });
  };

  const openAddToAnotherTeamDialog = useCallback(() => {
    if (!editRow?.userId) {
      addNotification({
        message: 'Missing user context',
        level: notificationStates.warning,
      });
      return;
    }
    const employee = employeesState.find((e) => e.userId === editRow.userId);
    if (!employee) return;

    setAddToAnotherTeamUser(employee);
    setAddToAnotherTeamCompany(DEMO_SELECTED_COMPANY);
    setAddToAnotherTeamRole(null);
    setAddToAnotherTeamTeams([]);
    const candidateTeams = DEMO_TEAM_OPTIONS.filter(
      (t) => !employee.teamIds.includes(t.value)
    );
    setAddToAnotherTeamCandidateTeams(candidateTeams);
    setAddToAnotherTeamCompanyTeamCount(DEMO_TEAM_OPTIONS.length);

    const activeTeamOpts = buildTeamOptionsFromIds(employee.teamIds);
    setAddToAnotherTeamActiveCompanyOptions(DEMO_COMPANY_OPTIONS);
    setAddToAnotherTeamActiveCompany(DEMO_SELECTED_COMPANY);
    setAddToAnotherTeamActiveTeamOptions(activeTeamOpts);
    const activeSelected =
      activeTeamOpts.find((t) => t.value === employee.activeTeamId) ||
      activeTeamOpts[0] ||
      null;
    setAddToAnotherTeamActiveTeam(activeSelected);
    setAddToAnotherTeamActiveBaseline({
      companyId: DEMO_SELECTED_COMPANY.value,
      teamId: activeSelected?.value || null,
    });

    setAddToAnotherTeamDialogVisible(true);
    menuRef.current?.hide();
  }, [editRow, employeesState]);

  const onAddToAnotherTeamCompanyChange = useCallback((option) => {
    setAddToAnotherTeamCompany(option);
    setAddToAnotherTeamTeams([]);
  }, []);

  const onAddToAnotherTeamTeamsChange = useCallback((option) => {
    setAddToAnotherTeamTeams(Array.isArray(option) ? option : []);
  }, []);

  const onAddToAnotherTeamActiveCompanyChange = useCallback(
    (option) => {
      if (!addToAnotherTeamUser?.userId || !option?.value) return;
      setAddToAnotherTeamActiveCompany(option);
      const teamOpts = buildTeamOptionsFromIds(addToAnotherTeamUser.teamIds);
      setAddToAnotherTeamActiveTeamOptions(teamOpts);
      setAddToAnotherTeamActiveTeam(teamOpts[0] || null);
    },
    [addToAnotherTeamUser]
  );

  // Dirty-check for the "Active company & department (tracking)" section —
  // reused both to gate the Save button (only enabled once something
  // actually changed) and inside the save handler below (only apply the
  // active-department update if there's really a change to persist).
  const addToAnotherTeamActiveInitialValues = useMemo(
    () => ({
      companyId: addToAnotherTeamActiveBaseline?.companyId ?? '',
      teamId: addToAnotherTeamActiveBaseline?.teamId ?? '',
    }),
    [addToAnotherTeamActiveBaseline]
  );
  const addToAnotherTeamActiveCurrentValues = {
    companyId: addToAnotherTeamActiveCompany?.value ?? '',
    teamId: addToAnotherTeamActiveTeam?.value ?? '',
  };
  const { hasChanges: hasActiveTrackingChanges } = useFormChanges(
    addToAnotherTeamActiveInitialValues,
    addToAnotherTeamActiveCurrentValues,
    ['companyId', 'teamId']
  );

  // Shared by both the regular and admin-only save paths below: applies the
  // "active company/department" selection locally if it actually changed.
  const applyActiveCompanyAndTeamIfChanged = useCallback(() => {
    const wantsActiveUpdate =
      addToAnotherTeamActiveBaseline &&
      addToAnotherTeamActiveCompany?.value &&
      addToAnotherTeamActiveTeam?.value &&
      hasActiveTrackingChanges;

    if (!wantsActiveUpdate) return;

    updateEmployee(addToAnotherTeamUser.userId, {
      activeTeamId: addToAnotherTeamActiveTeam.value,
    });
    addNotification({
      message: `Active company and department updated for time and activity: ${addToAnotherTeamActiveTeam.label}.`,
      level: notificationStates.success,
    });
  }, [
    addToAnotherTeamUser,
    addToAnotherTeamActiveBaseline,
    addToAnotherTeamActiveCompany,
    addToAnotherTeamActiveTeam,
    hasActiveTrackingChanges,
    updateEmployee,
  ]);

  const submitAddToAnotherTeam = useCallback(() => {
    if (!addToAnotherTeamUser?.userId) {
      addNotification({
        message: 'Please select company and user',
        level: notificationStates.warning,
      });
      return;
    }

    if (isExistingTeamAdmin) {
      applyActiveCompanyAndTeamIfChanged();
      resetAddToAnotherTeamDialog();
      return;
    }

    if (!addToAnotherTeamCompany?.value) {
      addNotification({
        message: 'Please select company and user',
        level: notificationStates.warning,
      });
      return;
    }
    if (!addToAnotherTeamRole?.value) {
      addNotification({
        message: 'Please select role',
        level: notificationStates.warning,
      });
      return;
    }

    const isAdminPromotion = addToAnotherTeamRole.value === 'Admin';
    if (!isAdminPromotion && addToAnotherTeamTeams.length === 0) {
      addNotification({
        message: 'Please select at least one department',
        level: notificationStates.warning,
      });
      return;
    }

    if (isAdminPromotion) {
      updateEmployee(addToAnotherTeamUser.userId, {
        role: 'Admin',
        teamIds: DEMO_TEAM_OPTIONS.map((t) => t.value),
      });
      addNotification({
        message:
          'User promoted to Admin and synced across company departments.',
        level: notificationStates.success,
      });
    } else {
      const newTeamIds = Array.from(
        new Set([
          ...addToAnotherTeamUser.teamIds,
          ...addToAnotherTeamTeams.map((t) => t.value),
        ])
      );
      updateEmployee(addToAnotherTeamUser.userId, {
        role: addToAnotherTeamRole.value,
        teamIds: newTeamIds,
      });
      addNotification({
        message: 'User added to selected departments successfully.',
        level: notificationStates.success,
      });
    }

    applyActiveCompanyAndTeamIfChanged();
    resetAddToAnotherTeamDialog();
  }, [
    addToAnotherTeamUser,
    isExistingTeamAdmin,
    addToAnotherTeamCompany,
    addToAnotherTeamRole,
    addToAnotherTeamTeams,
    applyActiveCompanyAndTeamIfChanged,
    updateEmployee,
    resetAddToAnotherTeamDialog,
  ]);

  const actionsMenuItems = [
    {
      label: 'View details',
      icon: <FiEye size={16} />,
      onClick: openMoreDetailsDialog,
    },
    {
      label: 'Edit employee',
      icon: <FiEdit2 size={16} />,
      onClick: openEditDialog,
    },
    {
      label: 'Add to another department',
      icon: <FiUserPlus size={16} />,
      onClick: openAddToAnotherTeamDialog,
    },
    {
      label: 'Change active department',
      icon: <FiRefreshCw size={16} />,
      onClick: openChangeActiveTeamDialog,
    },
    {
      label: 'Remove from department',
      icon: <FiTrash2 size={16} />,
      onClick: openDeleteConfirm,
    },
    {
      label: 'Remove from company',
      icon: <BsBuildings size={16} />,
      onClick: openRemoveFromCompanyConfirm,
    },
  ];

  // Edit dialog: hydrate local form fields from the row + its extended
  // details once, when the dialog opens for a given user.
  useLayoutEffect(() => {
    if (!editDialogVisible || !editRow?.userId || !extendedDetails) return;
    if (editFormHydratedRef.current === editRow.userId) return;
    editFormHydratedRef.current = editRow.userId;
    setEditName(editRow.name || '');
    setEditDeskId(extendedDetails.deskId || '');
    setEditTimeZone(extendedDetails.timeZone || '');
    setEditRole(editRow.role || '');
  }, [editDialogVisible, editRow, extendedDetails]);

  const editFormInitialValues = useMemo(() => {
    if (!editRow || !extendedDetails) {
      return { name: '', deskId: '', timeZone: '', role: '' };
    }
    return {
      name: editRow.name || '',
      deskId: extendedDetails.deskId || '',
      timeZone: extendedDetails.timeZone || '',
      role: editRow.role || '',
    };
  }, [editRow, extendedDetails]);
  const editFormCurrentValues = {
    name: editName,
    deskId: editDeskId,
    timeZone: editTimeZone,
    role: editRole,
  };
  const { hasChanges: hasEditFormChanges } = useFormChanges(
    editFormInitialValues,
    editFormCurrentValues,
    ['name', 'deskId', 'timeZone', 'role']
  );

  const saveEditDetails = () => {
    if (!editRow?.userId) {
      addNotification({
        message: 'Missing context (user)',
        level: notificationStates.warning,
      });
      return;
    }
    const newName = (editName || '').trim();
    if (!newName) {
      addNotification({
        message: 'Name cannot be empty',
        level: notificationStates.warning,
      });
      return;
    }
    const newDeskId = (editDeskId || '').trim();
    if (!newDeskId) {
      addNotification({
        message: 'Desk Id cannot be empty',
        level: notificationStates.warning,
      });
      return;
    }
    if (!hasEditFormChanges) {
      addNotification({
        message: 'No changes to save',
        level: notificationStates.warning,
      });
      return;
    }

    updateEmployee(editRow.userId, {
      name: newName,
      deskId: newDeskId,
      timeZone: (editTimeZone || '').trim(),
      role:
        editRole && ROLE_OPTIONS.some((o) => o.value === editRole)
          ? editRole
          : editRow.role,
    });

    setEditDialogVisible(false);
    setEditRow(null);
    setEditName('');
    setEditDeskId('');
    setEditTimeZone('');
    setEditRole('');
    editFormHydratedRef.current = null;
    toast.current?.show({
      severity: 'success',
      summary: 'Success',
      detail: 'Employee details updated successfully',
      life: 3000,
    });
  };

  const employeeDetailFields = useMemo(() => {
    if (!moreDetailsRosterRow || !extendedDetails) return [];
    return [
      {
        key: 'email',
        icon: <FiMail size={14} color="var(--blue-600)" />,
        iconBg: 'var(--blue-100)',
        label: 'Email',
        value: moreDetailsRosterRow.email,
        copyLabel: 'Email',
        fullWidth: true,
      },
      {
        key: 'employeeId',
        icon: <FiCreditCard size={14} color="var(--purple-600)" />,
        iconBg: 'var(--purple-100)',
        label: 'Employee ID',
        value: extendedDetails.employeeId,
        copyLabel: 'Employee ID',
      },
      {
        key: 'role',
        icon: <FiShield size={14} color="var(--success-600)" />,
        iconBg: 'var(--success-100)',
        label: 'Role',
        value: moreDetailsRosterRow.role,
      },
      {
        key: 'timeZone',
        icon: <FiGlobe size={14} color="var(--warning-600)" />,
        iconBg: 'var(--warning-100)',
        label: 'Time Zone',
        value: extendedDetails.timeZone,
      },
      {
        key: 'deskId',
        icon: <FiMonitor size={14} color="var(--gray-600)" />,
        iconBg: 'var(--gray-100)',
        label: 'Desk ID',
        value: extendedDetails.deskId,
      },
      {
        key: 'guid',
        icon: <FiHash size={14} color="var(--gray-600)" />,
        iconBg: 'var(--gray-100)',
        label: 'GUID',
        value: extendedDetails.guid,
        copyLabel: 'GUID',
        mono: true,
        truncate: true,
      },
      {
        key: 'joinedOn',
        icon: <FiCalendar size={14} color="var(--blue-600)" />,
        iconBg: 'var(--blue-100)',
        label: 'Joined On',
        value: formatJoinedOn(extendedDetails.createdAt),
      },
    ];
  }, [moreDetailsRosterRow, extendedDetails]);

  const onInviteEmployeeClick = useCallback(() => {
    history.push({
      pathname: appRoutes().addEmployee,
      search: mergeEmployeePageFiltersIntoSearch(location.search, {
        companyId: displaySelectedCompany?.value ?? '',
        teamId: displaySelectedTeam?.value ?? '',
      }),
    });
  }, [
    history,
    location.search,
    displaySelectedCompany?.value,
    displaySelectedTeam?.value,
  ]);

  const onAddDepartmentClick = useCallback(() => {
    setShowAddTeamModal(true);
  }, []);

  const onCloseAddTeamModal = useCallback(() => {
    setShowAddTeamModal(false);
  }, []);

  const onCloseMoreDetailsDialog = useCallback(() => {
    setMoreDetailsUserId(null);
  }, []);

  const onHideEditDialog = useCallback(() => {
    setEditDialogVisible(false);
    setEditRow(null);
    setEditName('');
    setEditDeskId('');
    setEditTimeZone('');
    setEditRole('');
    editFormHydratedRef.current = null;
  }, []);

  const onCancelEditDialog = useCallback(() => {
    setEditDialogVisible(false);
    editFormHydratedRef.current = null;
  }, []);

  const onEditDeskIdChange = useCallback((e) => {
    setEditDeskId(e.target.value);
  }, []);

  const onEditRoleChange = useCallback((e) => {
    setEditRole(e.value || '');
  }, []);

  const onEditTimeZoneChange = useCallback((e) => {
    setEditTimeZone(e.value || '');
  }, []);

  const onSearchTermChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  return (
    <EmployeeListView
      error={error}
      userProfile={userProfile}
      selectedCompany={displaySelectedCompany}
      selectedTeam={displaySelectedTeam}
      teamOptions={teamOptions}
      loadingTeams={false}
      onTeamChange={onTeamChange}
      roleFilter={roleFilter}
      roleFilterOptions={ROLE_FILTER_OPTIONS}
      onRoleFilterChange={setRoleFilter}
      showAddTeamButton={showAddTeamButton}
      onInviteEmployeeClick={onInviteEmployeeClick}
      onAddDepartmentClick={onAddDepartmentClick}
      onExportCSV={exportToCSV}
      roleCounts={roleCounts}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      toastRef={toast}
      deleteConfirmVisible={deleteConfirmVisible}
      onHideDeleteConfirm={resetDeleteConfirm}
      deleteConfirmMessage={deleteConfirmMessage}
      onConfirmDeleteUserFromTeam={confirmDeleteUserFromTeam}
      removeFromCompanyConfirmVisible={removeFromCompanyConfirmVisible}
      onHideRemoveFromCompanyConfirm={resetRemoveFromCompanyConfirm}
      removeFromCompanyMessage={removeFromCompanyMessage}
      onConfirmRemoveFromCompany={confirmRemoveFromCompany}
      removeFromCompanyPending={false}
      actionsMenuItems={actionsMenuItems}
      menuRef={menuRef}
      moreDetailsUserId={moreDetailsUserId}
      onCloseMoreDetailsDialog={onCloseMoreDetailsDialog}
      moreDetailsRosterRow={moreDetailsRosterRow}
      loadingExtendedDetails={loadingExtendedDetails}
      extendedDetailsError={extendedDetailsError}
      extendedDetailsQueryError={extendedDetailsQueryError}
      extendedDetails={extendedDetails}
      employeeDetailFields={employeeDetailFields}
      onFieldCopied={handleFieldCopied}
      onFieldCopyError={handleFieldCopyError}
      isExistingTeamAdmin={isExistingTeamAdmin}
      addToAnotherTeamDialogVisible={addToAnotherTeamDialogVisible}
      onCloseAddToAnotherTeamDialog={resetAddToAnotherTeamDialog}
      submittingAddToAnotherTeam={false}
      onSubmitAddToAnotherTeam={submitAddToAnotherTeam}
      loadingAddToAnotherTeamActiveSection={false}
      hasActiveTrackingChanges={hasActiveTrackingChanges}
      loadingAddToAnotherTeamCandidates={false}
      addToAnotherTeamCompany={addToAnotherTeamCompany}
      addToAnotherTeamRole={addToAnotherTeamRole}
      addToAnotherTeamTeams={addToAnotherTeamTeams}
      companyOptions={companyOptions}
      onAddToAnotherTeamCompanyChange={onAddToAnotherTeamCompanyChange}
      onAddToAnotherTeamTeamsChange={onAddToAnotherTeamTeamsChange}
      onAddToAnotherTeamRoleChange={setAddToAnotherTeamRole}
      addToAnotherTeamCandidateTeams={addToAnotherTeamCandidateTeams}
      addToAnotherTeamCompanyTeamCount={addToAnotherTeamCompanyTeamCount}
      addToAnotherTeamUser={addToAnotherTeamUser}
      addToAnotherTeamActiveCompanyOptions={
        addToAnotherTeamActiveCompanyOptions
      }
      addToAnotherTeamActiveCompany={addToAnotherTeamActiveCompany}
      onAddToAnotherTeamActiveCompanyChange={
        onAddToAnotherTeamActiveCompanyChange
      }
      addToAnotherTeamActiveTeamOptions={addToAnotherTeamActiveTeamOptions}
      addToAnotherTeamActiveTeam={addToAnotherTeamActiveTeam}
      onAddToAnotherTeamActiveTeamChange={setAddToAnotherTeamActiveTeam}
      changeActiveTeamDialogVisible={changeActiveTeamDialogVisible}
      onCloseChangeActiveTeamDialog={resetChangeActiveTeamDialog}
      submittingChangeActiveTeam={false}
      onSubmitChangeActiveTeam={submitChangeActiveTeam}
      loadingChangeActiveTeamModal={false}
      changeActiveTeamCompany={changeActiveTeamCompany}
      changeActiveTeamTeam={changeActiveTeamTeam}
      hasChangeActiveTeamPendingSave={hasChangeActiveTeamPendingSave}
      changeActiveTeamUser={changeActiveTeamUser}
      changeActiveTeamCompanyOptions={changeActiveTeamCompanyOptions}
      onChangeActiveTeamCompanySelect={onChangeActiveTeamCompanySelect}
      changeActiveTeamTeamOptions={changeActiveTeamTeamOptions}
      onChangeActiveTeamTeamChange={setChangeActiveTeamTeam}
      editDialogVisible={editDialogVisible}
      onHideEditDialog={onHideEditDialog}
      editRow={editRow}
      editRole={editRole}
      onCancelEditDialog={onCancelEditDialog}
      onSaveEditDetails={saveEditDetails}
      updateEmployeeDetailsPending={false}
      hasEditFormChanges={hasEditFormChanges}
      editDeskId={editDeskId}
      onEditDeskIdChange={onEditDeskIdChange}
      onEditRoleChange={onEditRoleChange}
      editTimeZone={editTimeZone}
      onEditTimeZoneChange={onEditTimeZoneChange}
      timezoneOptions={timezoneOptions}
      pickActiveTeamDialogVisible={pickActiveTeamDialogVisible}
      onClosePickActiveTeamDialog={resetPickActiveTeamDialog}
      removeUserFromTeamPending={false}
      onConfirmDeleteAfterPickingActiveTeam={
        confirmDeleteAfterPickingActiveTeam
      }
      selectedReplacementTeam={selectedReplacementTeam}
      replacementTeamOptions={replacementTeamOptions}
      onSelectedReplacementTeamChange={setSelectedReplacementTeam}
      companiesError={null}
      teamsError={null}
      rosterError={false}
      rosterQueryError={null}
      employees={employees}
      loadingEmployees={false}
      searchTerm={searchTerm}
      onSearchTermChange={onSearchTermChange}
      sortedEmployees={sortedEmployees}
      pagedEmployees={pagedEmployees}
      onSortClick={handleSortClick}
      sortedBy={sortedBy}
      reverseSort={reverseSort}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={PAGE_SIZE}
      onActionsButtonClick={onActionsButtonClick}
      userUID={userUID}
      IntegrationImportTabComponent={IntegrationImportTabDemoComponent}
      showAddTeamModal={showAddTeamModal}
      onCloseAddTeamModal={onCloseAddTeamModal}
      onTeamCreated={() => {}}
      addTeamDefaultCompanyId={addTeamDefaultCompanyId}
    />
  );
};
